import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from 'reactstrap';

import { messages } from '../../../_shared/i18n/messages';

import { PRIVACY_TYPES } from '../../constants';

import './PrivacyInput.scss';

const propTypes = {

	/**
	 * The name of the organization.
	 */
	orgName: PropTypes.string.isRequired,

	/**
	 * The privacy value.
	 *
	 * 0 = Only me, i.e. only me and org admins (DEPRECATED)
	 * 20 = Facilitators
	 * 40 = Classmates
	 * 60 = Public
	 */
	value: PropTypes.oneOf([
		PRIVACY_TYPES.ONLY_ME, // DEPRECATED
		PRIVACY_TYPES.FACILITATORS,
		PRIVACY_TYPES.CLASSMATES,
		PRIVACY_TYPES.PUBLIC,
	]),

	/**
	 * The max (i.e. least restrictive) allowed value for the privacy.
	 *
	 * 20 = Facilitators
	 * 40 = Classmates
	 * 60 = Public
	 */
	maxPrivacy: PropTypes.oneOf([
		PRIVACY_TYPES.FACILITATORS,
		PRIVACY_TYPES.CLASSMATES,
		PRIVACY_TYPES.PUBLIC,
	]),

	/**
	 * Callback to handle changes to the privacy value.
	 */
	onChange: PropTypes.func.isRequired,

};

const defaultProps = {
	maxPrivacy: PRIVACY_TYPES.PUBLIC,
};

const getPrivacyOptions = (intl, orgName = 'TechChange', maxPrivacy) => {

	const options = [{
		label: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_FACILITATORS_LABEL_TEXT),
		value: PRIVACY_TYPES.FACILITATORS,
		help: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_FACILITATORS_HELP_TEXT),
		icon: 'fa-user',
	}];

	// Check if the max (i.e. least restricted allowed) privacy value is 20, i.e. FACILITATORS
	if (maxPrivacy === PRIVACY_TYPES.FACILITATORS) {
		return options;
	}

	options.push({
		label: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_CLASSMATES_LABEL_TEXT),
		value: PRIVACY_TYPES.CLASSMATES,
		help: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_CLASSMATES_HELP_TEXT),
		icon: 'fa-users',
	});

	// Check if the max (i.e. least restricted allowed) privacy value is 40, i.e. CLASSMATES
	if (maxPrivacy === PRIVACY_TYPES.CLASSMATES) {
		return options;
	}

	options.push({
		label: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_PUBLIC_LABEL_TEXT),
		value: PRIVACY_TYPES.PUBLIC,
		help: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_PUBLIC_HELP_TEXT, { org: orgName }),
		icon: 'fa-globe-americas',
	});

	// Check if the max (i.e. least restricted allowed) privacy value is 60, i.e. PUBLIC
	if (maxPrivacy === PRIVACY_TYPES.PUBLIC) {
		return options;
	}

	// Otherwise, there are no restrictions on the options
	return options;

};

const PrivacyInput = (props) => {

	const {
		orgName,
		value,
		maxPrivacy,
		onChange,
	} = props;

	const intl = useIntl();

	const [isOpen, setIsOpen] = useState(false);

	const options = getPrivacyOptions(intl, orgName, maxPrivacy);

	// This privacy option is DEPRECATED so we only need to check for existing values!
	if (value === PRIVACY_TYPES.ONLY_ME) {

		options.unshift({
			label: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_ONLY_ME_LABEL_TEXT),
			value: PRIVACY_TYPES.ONLY_ME,
			help: intl.formatMessage(messages.TC_COMPONENTS_FORM_INPUT_PRIVACY_OPTION_ONLY_ME_HELP_TEXT),
			icon: 'fa-ban'
		});

	}

	const selectedOption =
		options.find((option) => option.value === value) || options[options.length - 1];

	return (
		<Dropdown
			className="tc-privacy-dropdown text-muted"
			isOpen={isOpen}
			toggle={() => setIsOpen((prev) => !prev)}>
			<DropdownToggle
				color="dark"
				outline>
				<i
					aria-hidden="true"
					className={`fas ${selectedOption.icon}`} />
				{' '}
				<span>{selectedOption.label}</span>
			</DropdownToggle>
			<DropdownMenu end>
				{options.map((option) => (
					<DropdownItem
						key={option.value}
						active={value === option.value}
						onClick={() => onChange(option.value)}>
						<div className="d-flex flex-column">
							<span>
								<i
									aria-hidden="true"
									className={`fas ${option.icon}`} />
								{` ${option.label}`}
							</span>
							<span>
								{option.help}
							</span>
						</div>
					</DropdownItem>
				))}
			</DropdownMenu>
		</Dropdown>
	);

};

PrivacyInput.propTypes = propTypes;
PrivacyInput.defaultProps = defaultProps;

export default PrivacyInput;
